import { type FieldMetadata, useInputControl } from "@conform-to/react";
import {
  Combobox as HeadlessCombobox,
  ComboboxButton as HeadlessComboboxButton,
  ComboboxInput as HeadlessComboboxInput,
  ComboboxOptions as HeadlessComboboxOptions,
} from "@headlessui/react";
import { Close } from "@winamp/icons/Close.js";
import { Loading } from "@winamp/icons/Loading.js";
import type { ComponentProps } from "react";
import { classNames } from "../../utils/classNames";
import { Button } from "../Button";
import { Input } from "../Input";
import { isComboboxAction, removeComboboxActionPrefix } from "./ComboboxAction";

export interface IComboboxBaseProps {
  "aria-label"?: string;
  autoFocus?: boolean;
  children?: ComponentProps<typeof HeadlessComboboxOptions>["children"];
  className?: string;
  optionsProps?: ComponentProps<typeof HeadlessComboboxOptions>;
  placeholder?: string;
  readOnly?: boolean;
  onBlur?: () => void;
  setQuery?: (query: string) => void;
}

export type IComboboxProps = IComboboxBaseProps & {
  onClose?: () => void;
  onAction?: (action: string) => void;
  clearable?: boolean;
  emptyOptionsMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  immediate?: boolean;
  loading?: boolean;
} & (
    | {
        multiple: true;
        value?: Array<string>;
        meta?: FieldMetadata<Array<string>>;
        onChange?: (value: Array<string>) => void;
        displayValue?: (value: Array<string>) => string;
        displayIcon?: (value: Array<string> | undefined) => React.ReactNode;
        virtual?: {
          options: Array<string>;
          disabled?: (value: string) => boolean;
        } | null;
      }
    | {
        multiple?: false;
        value?: string;
        meta?: FieldMetadata<string | null>;
        onChange?: (value: string) => void;
        displayValue?: (value: string) => string;
        displayIcon?: (value: string | undefined) => React.ReactNode;
        virtual?: {
          options: Array<string>;
          disabled?: (value: string) => boolean;
        } | null;
      }
  );

export function Combobox({
  "aria-label": ariaLabel,
  autoFocus,
  children: options,
  className,
  clearable,
  disabled,
  emptyOptionsMessage,
  onAction,
  onClose,
  onChange,
  onBlur,
  meta,
  placeholder,
  setQuery,
  value: propsValue,
  displayValue,
  displayIcon,
  invalid,
  readOnly,
  immediate,
  loading,
  multiple,
  optionsProps,
  virtual,
}: IComboboxProps) {
  const control = meta
    ? // biome-ignore lint/correctness/useHookAtTopLevel: If 'meta' is not provided then 'useInputControl' should not be called at all
      useInputControl(meta)
    : {
        blur: undefined,
        focus: undefined,
        value: propsValue,
      };

  const isEmptyOptions =
    !options || (Array.isArray(options) && options.length === 0);

  return (
    <HeadlessCombobox
      onChange={(value: string & Array<string>) => {
        const isAction = isComboboxAction(value);
        if ("change" in control && !isAction) {
          control.change(value);
        }

        if (isAction) {
          const cleanValue = removeComboboxActionPrefix(value);
          onAction?.(cleanValue);
        } else {
          onChange?.(value);
        }

        // Make sure the change event has been processed before blurring the control
        setTimeout(() => {
          control.blur?.();
          onBlur?.();
        }, 0);
      }}
      onClose={() => {
        setQuery?.("");
        onClose?.();
      }}
      value={control.value ?? (multiple ? [] : "")}
      defaultValue={control.value ?? (multiple ? [] : "")}
      disabled={disabled}
      immediate={!readOnly && immediate}
      multiple={multiple}
      virtual={virtual}
    >
      <div className={classNames("relative", className)} data-slot="control">
        <HeadlessComboboxInput
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          autoComplete="off"
          data-1p-ignore
          className={classNames([
            // Basic layout
            "group relative block w-full",

            // Set minimum height on input
            "min-h-11 [&>input]:min-h-11 sm:min-h-9 sm:[&>input]:min-h-9",

            // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
            "before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)]",

            // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
            "dark:before:hidden",

            // Hide default focus styles
            "focus:outline-none",

            // Focus ring
            "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:data-[focus]:ring-2 sm:after:data-[focus]:ring-focus",

            // Disabled state
            "data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none",
          ])}
          placeholder={placeholder}
          onChange={(event) => setQuery?.(event.currentTarget.value)}
          displayValue={
            displayValue as (value: string & Array<string>) => string
          }
          invalid={invalid}
          id={meta?.id}
          icon={displayIcon?.(
            control.value as (Array<string> & string) | undefined,
          )}
          as={Input}
          readOnly={readOnly}
        />
        {clearable && (
          <Button
            variant="ghost"
            icon
            className="absolute top-1.5 right-8"
            size="2xs"
            onClick={() => {
              if (multiple) {
                onChange?.([]);
                if ("change" in control) {
                  control.change([]);
                }
              } else {
                onChange?.("");
                if ("change" in control) {
                  control.change("");
                }
              }
            }}
          >
            <Close />
          </Button>
        )}
        <HeadlessComboboxButton
          className={classNames("group absolute inset-y-0 right-0 px-2", {
            hidden: readOnly,
          })}
          disabled={loading}
        >
          <svg
            className={classNames(
              "size-5 stroke-zinc-500 group-data-[disabled]:opacity-50 group-data-[disabled]:stroke-zinc-600 sm:size-4 dark:stroke-zinc-400",
              {
                hidden: loading,
              },
            )}
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Loading
            className={classNames("animate-spin", { hidden: !loading })}
          />
        </HeadlessComboboxButton>
        <HeadlessComboboxOptions
          anchor="bottom"
          transition
          {...optionsProps}
          className={(bag: unknown) =>
            classNames(
              // Base styles
              "w-[var(--input-width)] isolate min-w-[calc(var(--button-width)+1.75rem)] select-none scroll-py-1 rounded-xl p-1",

              // Transition styles
              "data-[leave]:ease-in data-[leave]:duration-100 data-[leave]:data-[closed]:opacity-0 ",

              // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
              "outline outline-1 outline-transparent focus:outline-none",

              // Handle scrolling when menu won't fit in the viewport
              "overflow-y-scroll overscroll-contain",

              // Popover background
              "bg-primary/75 backdrop-blur-xl",

              // Shadows
              "shadow-lg ring-1 ring-accent dark:ring-inset",

              "z-40",

              optionsProps
                ? typeof optionsProps.className === "function"
                  ? (optionsProps.className(bag) as string)
                  : (optionsProps.className as string)
                : "",

              { hidden: loading || isEmptyOptions },
            )
          }
        >
          {!!emptyOptionsMessage && isEmptyOptions ? (
            <div className="text-center text-secondary px-4 py-3 text-sm">
              {emptyOptionsMessage}
            </div>
          ) : (
            options
          )}
        </HeadlessComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}
